'use client'
import * as React from 'react'
import { Block, BlockChange } from '@basehub/data/replicache/index'
import { TreeManager } from '@basehub/utils/tree/index'
import _get from 'lodash.get'
import _isEqual from 'lodash.isequal'
import { clsx } from 'clsx'
import { title } from '~/lib/title'
import Link from 'next/link'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { useBranchOrCommitSegment } from '~/lib/commits/use-selected-branch-or-commit'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import {
  useGetTabPrefix,
  useTabsStore,
} from '../../(editor)/explore/components/tabs/store'
import { prettifyMimeType } from '../../(editor)/explore/components/blocks/media/utils'
import { formatBytes } from '~/lib/file-utils'
import { fromUnixTime } from 'date-fns'

export type PropertiesDiffProps = {
  change: BlockChange
  condition: 'removed' | 'added'
  treeNew: TreeManager<Block, NonNullable<unknown>> | undefined
  treeOld: TreeManager<Block, NonNullable<unknown>> | undefined
  changesTree: TreeManager<BlockChange, NonNullable<unknown>> | undefined
  isInFullView?: boolean
  wrapper: ({ children }: { children: React.ReactNode }) => React.ReactNode
  fallback?: React.ReactNode
}

export const PropertiesDiff = ({
  condition,
  change,
  changesTree,
  treeNew,
  treeOld,
  wrapper,
  fallback = null,
}: PropertiesDiffProps) => {
  const items = React.useMemo(() => {
    const _items: {
      label: string
      valueNode: React.ReactNode
      changed: boolean
    }[] = []
    const block =
      condition === 'added'
        ? change.current
        : (change.conflicting ?? change.previous)
    const match =
      condition === 'added'
        ? (change.conflicting ?? change.previous)
        : change.current

    if (!block) return _items

    const isInstanceChild = !!block.instanceChildTarget

    const properties: (
      | {
          path: string
          label: string
          valueRenderer?: Extract<ValueRenderer, 'custom'>
          renderer: (value: unknown) => React.ReactNode
        }
      | {
          path: string
          label: string
          valueRenderer?: Exclude<ValueRenderer, 'custom'>
        }
    )[] = [
      { path: 'slug', label: 'Slug' },
      { path: 'hidden', label: 'Hidden' },
      ...(!isInstanceChild
        ? [
            { path: 'apiName', label: 'API Name' },
            { path: 'searchConfig.index', label: 'Search Index' },
            { path: 'previewUrl', label: 'Preview URL' },
          ]
        : []),
    ]

    if (!isInstanceChild) {
      properties.push(
        ...Object.keys(block.constraints)
          .map((k) => {
            switch (block.type + '__' + k) {
              case `select__acceptedValues`:
              case `rich-text__allowedNodes`:
                return {
                  path: `constraints.${k}`,
                  label: prettifyKeyBestEffort(k),
                  valueRenderer: 'string[]' as const,
                }
              case `rich-text__allowedComponents`:
              case `reference__allowedComponents`:
              case `reference__acceptedTypes`:
              case `union__acceptedTypes`:
                return {
                  path: `constraints.${k}`,
                  label: prettifyKeyBestEffort(k),
                  valueRenderer: 'block[]' as const,
                }
              case `icon__allowedLibraries`:
              case `media__allowedMimeTypes`:
                return {
                  path: `constraints.${k}`,
                  label: prettifyKeyBestEffort(k),
                  valueRenderer: 'yaml:record' as const,
                }
              case `event__schema`:
                // skip if object is empty
                if (
                  'schema' in block.constraints &&
                  !Object.values(block.constraints.schema).length
                ) {
                  return null
                }

                return {
                  path: `constraints.${k}`,
                  label: prettifyKeyBestEffort(k),
                  valueRenderer: 'yaml' as const,
                }
              case `og-image__layersData`:
                // skip as we'll handle it later
                return null
              case `agent__access`:
              case `agent__customTools`:
                return null
              // return {
              //   path: `constraints.${k}`,
              //   label: prettifyKeyBestEffort(k),
              //   valueRenderer: 'yaml' as const,
              // }
              default:
                if (k === 'acceptedVariantSets') {
                  return {
                    path: `constraints.${k}`,
                    label: prettifyKeyBestEffort(k),
                    valueRenderer: 'block[]' as const,
                  }
                }
                return {
                  path: `constraints.${k}`,
                  label: prettifyKeyBestEffort(k),
                }
            }
          })
          .filter(Boolean)
      )
    }

    if (block.description !== undefined) {
      properties.push({ path: 'description', label: 'Description' })
    }

    switch (block.type) {
      case 'component':
        properties.push(
          ...Object.keys(block.typeOptions).map((k) => {
            if (k === 'displayImage') {
              return {
                path: `typeOptions.displayImage.url`,
                label: 'Display Image',
              }
            }
            return {
              path: `typeOptions.${k}`,
              label: prettifyKeyBestEffort(k),
            }
          })
        )
        break
      case 'code-snippet':
        properties.push({ path: 'value.language', label: 'Language' })
        break
      case 'date':
        if (!isInstanceChild) break
        properties.push({ path: 'typeOptions.useTime', label: 'Use Time' })
        break
      case 'media':
        properties.push({ path: 'value.fileName', label: 'File Name' })
        properties.push({ path: 'value.mimeType', label: 'MIME Type' })
        properties.push({
          path: 'value.fileSize',
          label: 'File Size',
          valueRenderer: 'custom',
          renderer: (value) => {
            if (typeof value === 'number') return formatBytes(value)
            return <>{value}</>
          },
        })
        properties.push({ path: 'value.altText', label: 'Alt Text' })
        properties.push({
          path: 'value.width',
          label: 'Width',
          valueRenderer: 'custom',
          renderer: (value) => {
            if (typeof value === 'number') return <>{value}px</>
            return <>{value}</>
          },
        })
        properties.push({
          path: 'value.height',
          label: 'Height',
          valueRenderer: 'custom',
          renderer: (value) => {
            if (typeof value === 'number') return <>{value}px</>
            return <>{value}</>
          },
        })
        properties.push({
          path: 'value.lastModified',
          label: 'Last Modified',
          valueRenderer: 'custom',
          renderer: (value) => {
            if (typeof value !== 'number') return <>{value}</>
            return fromUnixTime(Math.floor(value / 1000)).toDateString()
          },
        })
        break
      // case 'og-image':
      //   break
      case 'list':
      case 'instance':
        if (!isInstanceChild) break
        if (match) {
          properties.push({
            path: 'typeOptions.targetBlockId',
            label: 'Target Block ID',
            valueRenderer: 'block',
          })
        }
        if (block.type === 'list') {
          if (match) {
            // properties.push({
            //   path: 'typeOptions.targetBlockId',
            //   label: 'Target Block ID',
            //   valueRenderer: 'block',
            // })
          }
        }
        break
      case 'event':
        properties.push({
          path: 'value.schema',
          label: 'Schema',
          valueRenderer: 'yaml',
        })
        if (!isInstanceChild) break
        properties.push({
          path: 'typeOptions.view',
          label: 'Layout',
          valueRenderer: 'string',
        })
        break
      case 'agent':
        properties.push(
          ...Object.keys(block.typeOptions).map((k) => {
            return {
              path: `typeOptions.${k}`,
              label: prettifyKeyBestEffort(k),
            }
          })
        )
        break
      default:
        break
    }

    properties.forEach(({ path, label, ...rest }) => {
      const value = _get(block, path)
      const matchValue = _get(match, path)

      if (!match) {
        /**
         * If there's no match (deletes or creates), we don't show properties that haven't been changed (best effort).
         */
        if (
          value === undefined ||
          value === null ||
          value === '' ||
          value === false
        ) {
          return
        }
      }

      const item =
        rest.valueRenderer === 'custom'
          ? {
              value,
              valueRenderer: rest.valueRenderer || 'string',
              renderer: rest.renderer,
              changesTree,
              condition,
              treeNew,
              treeOld,
            }
          : {
              value,
              valueRenderer: rest.valueRenderer || 'string',
              changesTree,
              condition,
              treeNew,
              treeOld,
            }

      _items.push({
        label,
        valueNode: processValue(item),
        changed: _isEqual(value, matchValue) === false,
      })
    })

    return _items.sort((a, b) => {
      if (a.label < b.label) return -1
      if (a.label > b.label) return 1
      return 0
    })
  }, [change, changesTree, condition, treeNew, treeOld])

  const changed = items.some((i) => i.changed)

  if (!changed) return fallback
  return wrapper({
    children: (
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-col gap-1">
          {items.map((item) => {
            if (!item.changed) return null
            return (
              <Property
                key={item.label}
                condition={condition}
                label={item.label}
                valueNode={item.valueNode}
              />
            )
          })}
        </div>
      </div>
    ),
  })
}

const Property = ({
  condition,
  label,
  valueNode,
}: {
  condition: 'removed' | 'added'
  label: string
  valueNode: React.ReactNode
}) => {
  const boxColor =
    condition === 'removed' ? 'bg-red-diff/30' : 'bg-green-diff/30'

  return (
    <div className="text-xs">
      <span className="font-medium text-dark-muted">{label}:</span>{' '}
      <span className="font-mono">
        {valueNode === '' ? (
          <span
            className={clsx(
              boxColor,
              'px-0.5 inline-flex rounded italic text-dark-faint'
            )}
          >
            Unset
          </span>
        ) : (
          <span
            className={clsx(
              boxColor,
              'px-0.5 inline-flex rounded text-dark-dim flex-wrap gap-x-1'
            )}
          >
            {valueNode}
          </span>
        )}
      </span>
    </div>
  )
}

const prettifyKeyBestEffort = (key: string) =>
  key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^\w/, (c) => title(c))

type ValueRenderer =
  | 'string'
  | 'string[]'
  | 'block'
  | 'block[]'
  | 'yaml'
  | 'yaml:record'
  | 'custom'
const processValue = ({
  value,
  condition,
  treeNew,
  treeOld,
  ...rest
}:
  | {
      value: unknown
      valueRenderer: Exclude<ValueRenderer, 'custom'>
      condition: 'removed' | 'added'
      changesTree: TreeManager<BlockChange, NonNullable<unknown>> | undefined
      treeNew: TreeManager<Block, NonNullable<unknown>> | undefined
      treeOld: TreeManager<Block, NonNullable<unknown>> | undefined
    }
  | {
      value: unknown
      valueRenderer: Extract<ValueRenderer, 'custom'>
      renderer: (value: unknown) => React.ReactNode
      condition: 'removed' | 'added'
      changesTree: TreeManager<BlockChange, NonNullable<unknown>> | undefined
      treeNew: TreeManager<Block, NonNullable<unknown>> | undefined
      treeOld: TreeManager<Block, NonNullable<unknown>> | undefined
    }): React.ReactNode => {
  switch (rest.valueRenderer) {
    // @ts-ignore
    case 'custom': {
      return rest.renderer(value)
    }

    case 'string':
      return value?.toString() ?? ''

    case 'string[]': {
      const result = (value as string[] | null)?.join(', ')
      if (!result || result.length === 0) return ''
      return result
    }

    case 'block': {
      if (!value) return ''
      return (
        <InlineBlockLink
          blockId={value as string}
          condition={condition}
          treeNew={treeNew}
          treeOld={treeOld}
        />
      )
    }

    case 'block[]': {
      let hasAtLeastOne = false
      const result = (value as string[] | null)?.map((id, i, arr) => {
        hasAtLeastOne = true
        return (
          <InlineBlockLink
            blockId={id}
            condition={condition}
            treeNew={treeNew}
            treeOld={treeOld}
            hasComma={i !== arr.length - 1}
            key={i}
          />
        )
      })
      if (!hasAtLeastOne) return ''
      return result
    }

    case 'yaml:record':
    case 'yaml': {
      let arrayToRender
      if (
        typeof value === 'object' &&
        value !== null &&
        Object.keys(value).length > 0
      ) {
        arrayToRender =
          rest.valueRenderer === 'yaml'
            ? Object.values(value)
            : Object.entries(value).map(([key, value]) => ({
                [key]: value,
              }))
      } else if (Array.isArray(value) && value.length > 0) {
        arrayToRender = value
      } else {
        return ''
      }

      return (
        <span className="pl-1 pr-3 py-0.5 text-dark-dim flex flex-col gap-y-2">
          <YamlElements arr={arrayToRender} />
        </span>
      )
    }

    default:
      return value?.toString() ?? ''
  }
}

const InlineBlockLink = ({
  blockId,
  condition,
  treeNew,
  treeOld,
  hasComma,
}: {
  blockId: string
  condition: 'removed' | 'added'
  treeNew: TreeManager<Block, NonNullable<unknown>> | undefined
  treeOld: TreeManager<Block, NonNullable<unknown>> | undefined
  hasComma?: boolean
}) => {
  const { teamSlug, repoSlug, repoPage } = useDashboardSlugs()
  const { encodedSegment } = useBranchOrCommitSegment()
  const block =
    condition === 'added'
      ? (treeNew?.getNode(blockId)?.value ?? treeOld?.getNode(blockId)?.value)
      : (treeOld?.getNode(blockId)?.value ?? treeNew?.getNode(blockId)?.value)
  const addTab = useTabsStore((s) => s.addTab)
  const tabPrefix = useGetTabPrefix()

  if (!block) return null
  return (
    <span>
      <Link
        href={`/${teamSlug}/${repoSlug}/${repoPage}/${encodedSegment}/${tabPrefix}${idToSourceBlockId(
          block
        )}`}
        className="underline"
        onClick={() =>
          addTab({
            key: `${tabPrefix}${idToSourceBlockId(block)}`,
            partiallyOpen: true,
          })
        }
      >
        {block.title || 'Untitled'}
      </Link>
      {hasComma && ', '}
    </span>
  )
}

function YamlElements({
  arr,
  indent = 0,
}: {
  arr: unknown[]
  indent?: number
}): React.ReactNode {
  if (!Array.isArray(arr)) {
    throw new TypeError('Input must be an array')
  }

  const id = React.useId()
  const spaces = ' '.repeat(indent)
  const elements: React.ReactNode[] = []

  arr.forEach((item, mainIndex) => {
    if (Array.isArray(item)) {
      elements.push(
        <React.Fragment key={`${id}-${mainIndex}-array`}>
          {spaces}- <YamlElements arr={item} indent={indent + 2} />
        </React.Fragment>
      )
    } else if (typeof item === 'object' && item !== null) {
      const objElements: React.ReactNode[] = []
      Object.entries(item).forEach(([key, value], index) => {
        if (typeof value === 'string' && !value) return

        if (index !== 0) objElements.push('  ')
        if (Array.isArray(value)) {
          objElements.push(
            <React.Fragment key={key}>
              {spaces}
              <span className="font-medium text-dark-bright">{key}</span>:{'\n'}
              <YamlElements arr={value} indent={indent + 4} />
            </React.Fragment>
          )
        } else {
          const formattedValue = formatJSPrimitive(value)
          objElements.push(
            <React.Fragment key={key}>
              {spaces}
              <span className="font-medium text-dark-bright">{key}</span>:{' '}
              {formattedValue}
              {'\n'}
            </React.Fragment>
          )
        }
      })
      elements.push(
        <span
          key={`${id}-${mainIndex}-elements`}
          style={{
            order:
              'order' in item && typeof item.order === 'number'
                ? item.order
                : undefined,
          }}
          className="whitespace-pre-wrap block"
        >
          {spaces}- {objElements}
        </span>
      )
    } else {
      const formattedValue = formatJSPrimitive(item)
      elements.push(
        <React.Fragment key={`${id}-${mainIndex}-formatted`}>
          {spaces}- {formattedValue}
          {'\n'}
        </React.Fragment>
      )
    }
  })

  return elements
}

function formatJSPrimitive(value: unknown) {
  if (typeof value === 'string') {
    // Cleanup text if matches mimeType syntax
    if (/^\w+\/.+/.test(value)) {
      return prettifyMimeType(value)
    }

    // Add quotes if string contains special characters
    // eslint-disable-next-line no-useless-escape
    if (/[:#{}\[\],&*#?|\-<>=!%@`]/.test(value) || /^\s|\s$/.test(value)) {
      return `"${value.replace(/"/g, '\\"')}"`
    }
    return value
  }
  if (value === null) return 'null'
  if (value === undefined) return '~'
  return String(value)
}
